import React, { useState } from "react";
import { cn } from "@/lib/utils";
import useAvailabilityChange from "../../hooks/mutations/useAvailabilityChange";
import { useDoctorAvailability } from "../../hooks/queries/useDoctorAvailability";
import {
  Absence,
  DEFAULT_GRID_LABEL,
  DEFAULT_SLOT_MINUTES,
  DayOfWeek,
  WeeklyAvailability,
} from "../../services/availability/types";
import AbsenceFormModal from "./AbsenceFormModal";
import AbsenceList from "./AbsenceList";
import AvailabilityImpactModal from "./AvailabilityImpactModal";
import WeeklyRangeFormModal from "./WeeklyRangeFormModal";
import WeeklySchedule from "./WeeklySchedule";

/** Fenêtre de saisie ouverte : aucune, une plage ou une absence. */
type FormState =
  | { mode: "closed" }
  | { mode: "weekly"; range: WeeklyAvailability | null; day: DayOfWeek }
  | { mode: "absence"; absence: Absence | null };

const CLOSED: FormState = { mode: "closed" };

const SECONDARY_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 hover:border-emerald-200";

type SectionCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
};

const SectionCard: React.FC<SectionCardProps> = ({ eyebrow, title, description, action, className, children }) => (
  <section
    className={cn(
      "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both",
      className
    )}
  >
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-5 py-4">
      <div>
        <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">{eyebrow}</p>
        <h2 className="mt-1 text-base font-semibold text-slate-800">{title}</h2>
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
      {action}
    </div>
    {children}
  </section>
);

type DoctorAvailabilityPanelProps = {
  doctorId: number;
  /** Faux : même affichage, sans actions (secrétaire, autre médecin). */
  editable: boolean;
};

/**
 * Disponibilité d'un médecin : semaine type et absences. Chaque modification
 * est enregistrée immédiatement ; celle qui touche des RDV futurs est confirmée
 * dans une fenêtre qui les liste (D5). Utilisé dans le Profil du médecin et sur
 * la page de disponibilité ouverte depuis l'écran Médecins.
 */
const DoctorAvailabilityPanel: React.FC<DoctorAvailabilityPanelProps> = ({ doctorId, editable }) => {
  const { data: availability, isLoading } = useDoctorAvailability(doctorId);
  const [form, setForm] = useState<FormState>(CLOSED);
  const closeForm = () => setForm(CLOSED);
  const change = useAvailabilityChange(doctorId, closeForm);

  const slotMinutes = availability?.slotMinutes ?? DEFAULT_SLOT_MINUTES;

  if (isLoading) {
    return <p className="text-sm text-slate-500">Chargement de la disponibilité…</p>;
  }

  return (
    <div className="space-y-6">
      {availability && !availability.configured && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 animate-in fade-in duration-500 fill-mode-both">
          <i className="bx bx-info-circle mt-0.5 text-lg" aria-hidden />
          <p>
            <span className="font-semibold">Disponibilité non renseignée</span> : les RDV sont proposés{" "}
            {DEFAULT_GRID_LABEL}.
          </p>
        </div>
      )}

      {availability && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          <SectionCard
            className="xl:col-span-3 delay-100"
            eyebrow="Semaine type"
            title="Heures de consultation"
            description={`Plages répétées chaque semaine, par pas de ${slotMinutes} min.`}
            action={
              editable && (
                <button
                  type="button"
                  className={SECONDARY_BUTTON_CLASS}
                  onClick={() => setForm({ mode: "weekly", range: null, day: "MONDAY" })}
                >
                  <i className="bx bx-plus-circle text-base" /> Ajouter une plage
                </button>
              )
            }
          >
            <WeeklySchedule
              weekly={availability.weekly}
              onAdd={editable ? (day) => setForm({ mode: "weekly", range: null, day }) : undefined}
              onEdit={
                editable ? (range) => setForm({ mode: "weekly", range, day: range.dayOfWeek }) : undefined
              }
            />
          </SectionCard>

          <SectionCard
            className="xl:col-span-2 delay-200"
            eyebrow="Absences"
            title="Congés et déplacements"
            description="Journées entières sans consultation."
            action={
              editable && (
                <button
                  type="button"
                  className={SECONDARY_BUTTON_CLASS}
                  onClick={() => setForm({ mode: "absence", absence: null })}
                >
                  <i className="bx bx-plus-circle text-base" /> Nouvelle absence
                </button>
              )
            }
          >
            <AbsenceList
              absences={availability.absences}
              onEdit={editable ? (absence) => setForm({ mode: "absence", absence }) : undefined}
            />
          </SectionCard>
        </div>
      )}

      {form.mode === "weekly" && (
        <WeeklyRangeFormModal
          range={form.range}
          isLastRange={availability?.weekly.length === 1}
          defaultDay={form.day}
          slotMinutes={slotMinutes}
          isSaving={change.isWorking}
          onSubmit={(request) =>
            change.request(
              form.range
                ? { kind: "updateWeekly", weeklyId: form.range.id, request }
                : { kind: "createWeekly", request }
            )
          }
          onDelete={(range) => change.request({ kind: "deleteWeekly", weeklyId: range.id })}
          onClose={closeForm}
        />
      )}

      {form.mode === "absence" && (
        <AbsenceFormModal
          absence={form.absence}
          isSaving={change.isWorking}
          onSubmit={(request) =>
            change.request(
              form.absence
                ? { kind: "updateAbsence", absenceId: form.absence.id, request }
                : { kind: "createAbsence", request }
            )
          }
          onDelete={(absence) => change.request({ kind: "deleteAbsence", absenceId: absence.id })}
          onClose={closeForm}
        />
      )}

      {change.pending && (
        <AvailabilityImpactModal
          appointments={change.pending.appointments}
          isSaving={change.isWorking}
          onConfirm={change.confirmPending}
          onCancel={change.cancelPending}
        />
      )}
    </div>
  );
};

export default DoctorAvailabilityPanel;
